import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Building2, Plus, Users, FolderOpen, Store, UserPlus, ChevronRight, Shield, RefreshCw,
  MoreVertical, Pencil, Power, Pause, Play, Trash2, CircleDot, Upload, X, FileText, Image as ImageIcon
} from 'lucide-react';

/**
 * Logo upload field — replaces raw URL input.
 * Accepts: .jpg, .jpeg, .png, .pdf (≤ 2 MB). Converts to a data URL via the
 * backend so the parent component can PUT the same `logo_url` field that's
 * already stored on the tenant/portfolio record.
 */
function LogoUploadField({ value, onChange, api, headers, testIdPrefix = 'logo' }) {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef(null);

  const isPdf = value && value.startsWith('data:application/pdf');
  const isImage = value && (value.startsWith('data:image/') || /^https?:\/\//.test(value));

  const handleFile = async (file) => {
    if (!file) return;
    const MAX = 2 * 1024 * 1024;
    if (file.size > MAX) {
      toast.error('File too large (max 2 MB).');
      return;
    }
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
      toast.error('Only .jpg, .jpeg, .png, .pdf allowed.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${api}/upload/logo`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.file_url);
      setFileName(file.name);
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const clear = () => { onChange(''); setFileName(''); if (inputRef.current) inputRef.current.value = ''; };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-upload-field`}>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
        onChange={e => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
      {value ? (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-200"
          data-testid={`${testIdPrefix}-preview`}>
          {isImage ? (
            <img src={value} alt="Logo preview" className="h-12 w-12 object-contain rounded bg-white/5 flex-shrink-0" />
          ) : isPdf ? (
            <div className="h-12 w-12 rounded bg-[#1E2A33] flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-[#E8643E]" />
            </div>
          ) : (
            <div className="h-12 w-12 rounded bg-[#1E2A33] flex items-center justify-center flex-shrink-0">
              <ImageIcon className="w-6 h-6 text-stone-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#3A1F12] truncate font-medium">{fileName || (isPdf ? 'Logo (PDF)' : 'Logo')}</p>
            <p className="text-[10px] text-stone-500 truncate">{value.startsWith('data:') ? 'Uploaded file' : value}</p>
          </div>
          <Button type="button" size="sm" variant="ghost"
            onClick={() => inputRef.current?.click()} disabled={uploading}
            className="text-stone-500 hover:text-[#3A1F12] h-7 px-2"
            data-testid={`${testIdPrefix}-replace-btn`}>Replace</Button>
          <Button type="button" size="sm" variant="ghost"
            onClick={clear} disabled={uploading}
            className="text-stone-500 hover:text-red-400 h-7 px-2"
            data-testid={`${testIdPrefix}-clear-btn`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 py-6 px-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${uploading ? 'opacity-60 pointer-events-none' : 'border-stone-200 hover:border-amber-400/40 hover:bg-amber-400/5'}`}
          data-testid={`${testIdPrefix}-dropzone`}
        >
          {uploading ? (
            <RefreshCw className="w-5 h-5 text-[#E8643E] animate-spin" />
          ) : (
            <Upload className="w-5 h-5 text-stone-500" />
          )}
          <div className="text-center">
            <p className="text-sm text-[#3A1F12] font-medium">{uploading ? 'Uploading…' : 'Upload Logo'}</p>
            <p className="text-[11px] text-stone-500 mt-0.5">Drag & drop or click · .jpg .jpeg .png .pdf · max 2 MB</p>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_STYLES = {
  active:   { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', dot: 'bg-emerald-400' },
  inactive: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', dot: 'bg-red-400' },
  paused:   { bg: 'bg-amber-500/10', text: 'text-[#E8643E]', border: 'border-amber-500/20', dot: 'bg-amber-400' },
  deleted:  { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', dot: 'bg-gray-400' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.active;
  return (
    <Badge className={`${s.bg} ${s.text} ${s.border} text-[10px] font-medium gap-1`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

export default function SuperAdminDashboard() {
  const { getHeaders, API } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createTenantOpen, setCreateTenantOpen] = useState(false);
  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [editTenantOpen, setEditTenantOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tenantForm, setTenantForm] = useState({ name: '', logo_url: '', preset: 'full_platform', initial_bu_limit: 1 });
  const [editForm, setEditForm] = useState({ tenant_id: '', name: '', logo_url: '' });
  const [adminForm, setAdminForm] = useState({ tenant_id: '', name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/super/tenants`, { headers: getHeaders() });
      setTenants(res.data);
    } catch { toast.error('Failed to load tenants'); }
    finally { setLoading(false); }
  }, [API, getHeaders]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const handleCreateTenant = async () => {
    if (!tenantForm.name.trim()) { toast.error('Tenant name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/super/tenants`, tenantForm, { headers: getHeaders() });
      toast.success(`Tenant "${tenantForm.name}" created`);
      setCreateTenantOpen(false);
      setTenantForm({ name: '', logo_url: '', preset: 'full_platform', initial_bu_limit: 1 });
      loadTenants();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleEditTenant = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super/tenants/${editForm.tenant_id}`, { name: editForm.name, logo_url: editForm.logo_url }, { headers: getHeaders() });
      toast.success('Tenant updated');
      setEditTenantOpen(false);
      loadTenants();
      if (selectedTenant === editForm.tenant_id) loadTenantDetail(editForm.tenant_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    try {
      await axios.put(`${API}/super/tenants/${tenantId}/status`, { status: newStatus }, { headers: getHeaders() });
      toast.success(`Tenant ${newStatus === 'deleted' ? 'deleted' : `set to ${newStatus}`}`);
      loadTenants();
      if (selectedTenant === tenantId) loadTenantDetail(tenantId);
      setDeleteConfirm(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateAdmin = async () => {
    if (!adminForm.tenant_id) { toast.error('Select a tenant'); return; }
    if (!adminForm.name.trim()) { toast.error('Name is required'); return; }
    if (!adminForm.email.trim()) { toast.error('Email is required'); return; }
    if (adminForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/super/tenant-admin`, adminForm, { headers: getHeaders() });
      toast.success(`Admin "${adminForm.email}" created`);
      setCreateAdminOpen(false);
      setAdminForm({ tenant_id: '', name: '', email: '', password: '' });
      loadTenants();
      if (selectedTenant === adminForm.tenant_id) loadTenantDetail(adminForm.tenant_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const loadTenantDetail = async (tenantId) => {
    setSelectedTenant(tenantId);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/super/tenant/${tenantId}`, { headers: getHeaders() });
      setTenantDetail(res.data);
    } catch { toast.error('Failed to load details'); setTenantDetail(null); }
    finally { setDetailLoading(false); }
  };

  const openEdit = (t) => {
    setEditForm({ tenant_id: t.tenant_id, name: t.name, logo_url: t.logo_url || '' });
    setEditTenantOpen(true);
  };

  const totalOutlets = tenants.reduce((s, t) => s + t.outlet_count, 0);
  const totalAdmins = tenants.reduce((s, t) => s + t.admin_count, 0);
  const totalManagers = tenants.reduce((s, t) => s + t.manager_count, 0);

  return (
    <div data-testid="super-admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#3A1F12] tracking-tight">Tenant Management</h1>
          <p className="text-sm text-stone-500 mt-1">Manage all tenants, admins, and onboarding</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setCreateTenantOpen(true)}
            className="bg-[#E8643E] hover:bg-[#C45A41] text-white font-medium" data-testid="create-tenant-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Tenant
          </Button>
          <Button size="sm" onClick={() => setCreateAdminOpen(true)}
            className="bg-white/10 hover:bg-white/15 text-[#3A1F12] border border-white/10" data-testid="create-admin-btn">
            <UserPlus className="w-4 h-4 mr-1.5" /> New Admin
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard label="Tenants" value={tenants.length} icon={Shield} />
        <StatCard label="Outlets" value={totalOutlets} icon={Store} />
        <StatCard label="Admins" value={totalAdmins} icon={Users} />
        <StatCard label="Managers" value={totalManagers} icon={Users} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 text-[#E8643E] animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tenant List */}
          <div className="lg:col-span-1 space-y-2" data-testid="tenant-list">
            <p className="text-xs uppercase tracking-wider text-stone-500 font-medium mb-3">All Tenants</p>
            {tenants.map(t => (
              <div key={t.tenant_id}
                className={`relative p-3 rounded-lg border transition-all cursor-pointer ${
                  selectedTenant === t.tenant_id
                    ? 'bg-[#FCE7D6] border-amber-400/30 ring-1 ring-amber-400/20'
                    : 'bg-white border-stone-200 hover:border-[#2E3A43]'
                }`}
                data-testid={`tenant-card-${t.tenant_id}`}
              >
                <div className="flex items-start justify-between" onClick={() => loadTenantDetail(t.tenant_id)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold truncate ${selectedTenant === t.tenant_id ? 'text-[#E8643E]' : 'text-[#3A1F12]'}`}>
                        {t.name}
                      </p>
                      <StatusBadge status={t.status} />
                    </div>
                    <p className="text-[11px] text-stone-500 mt-0.5 font-mono">{t.tenant_id}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] text-stone-500"><Store className="w-3 h-3 inline mr-0.5" /> {t.outlet_count}</span>
                      <span className="text-[10px] text-stone-500"><Users className="w-3 h-3 inline mr-0.5" /> {t.admin_count + t.manager_count}</span>
                      <span className="text-[10px] text-stone-500"><FolderOpen className="w-3 h-3 inline mr-0.5" /> {t.portfolio_count}</span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 rounded hover:bg-white/10 text-stone-500 hover:text-[#3A1F12]" data-testid={`tenant-actions-${t.tenant_id}`}
                        onClick={e => e.stopPropagation()}>
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white border-stone-200 text-[#3A1F12] min-w-[160px]">
                      <DropdownMenuItem onClick={() => openEdit(t)} className="text-[#3A1F12] focus:bg-white/10 focus:text-[#3A1F12]">
                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-[#1E2A33]" />
                      {t.status !== 'active' && (
                        <DropdownMenuItem onClick={() => handleStatusChange(t.tenant_id, 'active')} className="text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-400">
                          <Play className="w-3.5 h-3.5 mr-2" /> Activate
                        </DropdownMenuItem>
                      )}
                      {t.status === 'active' && (
                        <DropdownMenuItem onClick={() => handleStatusChange(t.tenant_id, 'paused')} className="text-[#E8643E] focus:bg-amber-500/10 focus:text-[#E8643E]">
                          <Pause className="w-3.5 h-3.5 mr-2" /> Pause
                        </DropdownMenuItem>
                      )}
                      {t.status !== 'inactive' && t.status !== 'deleted' && (
                        <DropdownMenuItem onClick={() => handleStatusChange(t.tenant_id, 'inactive')} className="text-red-400 focus:bg-red-500/10 focus:text-red-400">
                          <Power className="w-3.5 h-3.5 mr-2" /> Deactivate
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-[#1E2A33]" />
                      <DropdownMenuItem onClick={() => setDeleteConfirm(t)} className="text-red-400 focus:bg-red-500/10 focus:text-red-400">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>

          {/* Tenant Detail */}
          <div className="lg:col-span-2" data-testid="tenant-detail-panel">
            {!selectedTenant ? (
              <div className="flex items-center justify-center h-64 text-stone-500 text-sm">Select a tenant to view details</div>
            ) : detailLoading ? (
              <div className="flex items-center justify-center h-64"><RefreshCw className="w-5 h-5 text-[#E8643E] animate-spin" /></div>
            ) : tenantDetail ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-[#3A1F12]">{tenantDetail.name}</h2>
                    <p className="text-xs text-stone-500 font-mono">{tenantDetail.tenant_id}</p>
                  </div>
                  <StatusBadge status={tenantDetail.status} />
                </div>
                <DetailSection title="Admins" icon={Shield} items={tenantDetail.admins} renderItem={a => (
                  <div key={a.id} className="flex items-center justify-between py-1.5 border-b border-stone-200/50 last:border-0">
                    <div><p className="text-sm text-[#3A1F12] font-medium">{a.name}</p><p className="text-[11px] text-stone-500">{a.email}</p></div>
                  </div>
                )} emptyText="No admins. Create one using the button above." />
                <DetailSection title="Outlets" icon={Building2} items={tenantDetail.outlets} renderItem={o => (
                  <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-stone-200/50 last:border-0">
                    <p className="text-sm text-[#3A1F12]">{o.name}</p>
                    <Badge variant="outline" className="text-[10px] border-stone-200 text-stone-500">{o.status || 'active'}</Badge>
                  </div>
                )} emptyText="No outlets yet." />
                <DetailSection title="Managers" icon={Users} items={tenantDetail.managers} renderItem={m => (
                  <div key={m.id} className="flex items-center justify-between py-1.5 border-b border-stone-200/50 last:border-0">
                    <div><p className="text-sm text-[#3A1F12] font-medium">{m.name}</p><p className="text-[11px] text-stone-500">{m.email}</p></div>
                  </div>
                )} emptyText="No managers yet." />
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Create Tenant Dialog */}
      <Dialog open={createTenantOpen} onOpenChange={setCreateTenantOpen}>
        <DialogContent className="bg-white border-stone-200 text-[#3A1F12]">
          <DialogHeader>
            <DialogTitle className="text-[#3A1F12]">Create New Tenant</DialogTitle>
            <DialogDescription className="text-stone-500">Set up a new client tenant with auto-generated portfolio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-stone-500 text-xs">Tenant Name *</Label>
              <Input value={tenantForm.name} onChange={e => setTenantForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Skyline Restaurants" className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1" data-testid="tenant-name-input" />
            </div>
            <div>
              <Label className="text-stone-500 text-xs mb-1.5 block">Upload Logo</Label>
              <LogoUploadField
                value={tenantForm.logo_url}
                onChange={(url) => setTenantForm(f => ({ ...f, logo_url: url }))}
                api={API}
                headers={getHeaders()}
                testIdPrefix="create-tenant-logo"
              />
            </div>
            <div>
              <Label className="text-stone-500 text-xs mb-1.5 block">Preset</Label>
              <div className="flex gap-2">
                <button type="button"
                        data-testid="preset-full-platform"
                        onClick={() => setTenantForm(f => ({ ...f, preset: 'full_platform' }))}
                        className={`flex-1 px-3 py-2 rounded-md text-xs border transition
                          ${tenantForm.preset === 'full_platform'
                            ? 'bg-[#E8643E] text-white border-[#E8643E]'
                            : 'bg-white text-[#3A1F12] border-stone-200 hover:border-[#E8643E]'}`}>
                  Full Platform
                  <div className="text-[10px] opacity-70 mt-0.5 font-normal">Every module. Unlimited outlets.</div>
                </button>
                <button type="button"
                        data-testid="preset-loyalty-only"
                        onClick={() => setTenantForm(f => ({ ...f, preset: 'loyalty_only' }))}
                        className={`flex-1 px-3 py-2 rounded-md text-xs border transition
                          ${tenantForm.preset === 'loyalty_only'
                            ? 'bg-[#E8643E] text-white border-[#E8643E]'
                            : 'bg-white text-[#3A1F12] border-stone-200 hover:border-[#E8643E]'}`}>
                  Loyalty Only
                  <div className="text-[10px] opacity-70 mt-0.5 font-normal">Only Loyalty + Users + Outlets.</div>
                </button>
              </div>
            </div>
            {tenantForm.preset === 'loyalty_only' && (
              <div data-testid="preset-loyalty-only-bu">
                <Label className="text-stone-500 text-xs">Included Business Units *</Label>
                <Input type="number" min={1} max={100}
                       value={tenantForm.initial_bu_limit}
                       onChange={e => setTenantForm(f => ({ ...f, initial_bu_limit: Number(e.target.value) || 1 }))}
                       className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1"
                       data-testid="tenant-initial-bu-limit" />
                <div className="text-[11px] text-stone-500 mt-1">
                  Each Business Unit = 1 outlet. Additional units are granted via activation code.
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateTenantOpen(false)} className="text-stone-500 hover:text-[#3A1F12]">Cancel</Button>
            <Button onClick={handleCreateTenant} disabled={saving} className="bg-[#E8643E] hover:bg-[#C45A41] text-white font-medium" data-testid="confirm-create-tenant">
              {saving ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editTenantOpen} onOpenChange={setEditTenantOpen}>
        <DialogContent className="bg-white border-stone-200 text-[#3A1F12]">
          <DialogHeader>
            <DialogTitle className="text-[#3A1F12]">Edit Tenant</DialogTitle>
            <DialogDescription className="text-stone-500">Update tenant details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-stone-500 text-xs">Tenant Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1" data-testid="edit-tenant-name" />
            </div>
            <div>
              <Label className="text-stone-500 text-xs mb-1.5 block">Upload Logo</Label>
              <LogoUploadField
                value={editForm.logo_url}
                onChange={(url) => setEditForm(f => ({ ...f, logo_url: url }))}
                api={API}
                headers={getHeaders()}
                testIdPrefix="edit-tenant-logo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTenantOpen(false)} className="text-stone-500 hover:text-[#3A1F12]">Cancel</Button>
            <Button onClick={handleEditTenant} disabled={saving} className="bg-[#E8643E] hover:bg-[#C45A41] text-white font-medium" data-testid="confirm-edit-tenant">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={createAdminOpen} onOpenChange={setCreateAdminOpen}>
        <DialogContent className="bg-white border-stone-200 text-[#3A1F12]">
          <DialogHeader>
            <DialogTitle className="text-[#3A1F12]">Create Client Admin</DialogTitle>
            <DialogDescription className="text-stone-500">Create an admin user for a specific tenant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-stone-500 text-xs">Tenant *</Label>
              <Select value={adminForm.tenant_id} onValueChange={v => setAdminForm(f => ({ ...f, tenant_id: v }))}>
                <SelectTrigger className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1" data-testid="admin-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                <SelectContent className="bg-white border-stone-200">
                  {tenants.filter(t => t.status === 'active').map(t => (
                    <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-[#3A1F12]">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-stone-500 text-xs">Full Name *</Label>
              <Input value={adminForm.name} onChange={e => setAdminForm(f => ({ ...f, name: e.target.value }))}
                placeholder="John Doe" className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1" data-testid="admin-name-input" />
            </div>
            <div>
              <Label className="text-stone-500 text-xs">Email *</Label>
              <Input value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))}
                placeholder="admin@client.com" className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1" data-testid="admin-email-input" />
            </div>
            <div>
              <Label className="text-stone-500 text-xs">Password * (min 6 chars)</Label>
              <Input type="password" value={adminForm.password} onChange={e => setAdminForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Secure password" className="bg-stone-50 border-stone-200 text-[#3A1F12] mt-1" data-testid="admin-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateAdminOpen(false)} className="text-stone-500 hover:text-[#3A1F12]">Cancel</Button>
            <Button onClick={handleCreateAdmin} disabled={saving} className="bg-[#E8643E] hover:bg-[#C45A41] text-white font-medium" data-testid="confirm-create-admin">
              {saving ? 'Creating...' : 'Create Admin'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-white border-stone-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#3A1F12]">Delete Tenant: {deleteConfirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-500">
              This will remove access to all data for this tenant. Users will no longer be able to log in. Data remains intact but inaccessible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-stone-200 text-stone-500 hover:text-[#3A1F12] hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleStatusChange(deleteConfirm?.tenant_id, 'deleted')}
              className="bg-red-600 hover:bg-red-700 text-[#3A1F12]" data-testid="confirm-delete-tenant">
              Delete Tenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-3" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">{label}</p>
          <p className="text-xl font-bold text-[#3A1F12] mt-0.5">{value}</p>
        </div>
        <div className="w-8 h-8 rounded-lg bg-[#FCE7D6] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#E8643E]" />
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, icon: Icon, items, renderItem, emptyText }) {
  return (
    <Card className="bg-white border-stone-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-stone-500 font-medium flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" /> {title} ({items?.length || 0})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {items?.length > 0 ? <div className="space-y-1.5">{items.map(renderItem)}</div>
          : <p className="text-xs text-stone-500">{emptyText}</p>}
      </CardContent>
    </Card>
  );
}
